import { atom } from 'jotai';

/**
 * Jotai atoms for MFA challenge state management
 *
 * The MFA challenge flow is triggered when:
 * 1. User has MFA enrolled (Firebase Multi-Factor)
 * 2. User attempts to sign in with email/password
 * 3. Firebase throws auth/multi-factor-auth-required error
 * 4. We capture the resolver and render MfaChallengeForm on /login
 *
 * This state MUST be cleared after:
 * - Successful MFA verification
 * - Sign out
 * - Navigation away from /login (without completing)
 */
const initialState = {
    isPending: false,
    resolver: null,
    selectedMethod: null,
    phoneVerificationId: null,
};
/**
 * Base state atom for MFA challenge.
 */
const mfaChallengeBaseAtom = atom(initialState);
/**
 * Derived atom: User's enrolled MFA factors from the resolver.
 * Transforms Firebase's MultiFactorInfo into UI-friendly format.
 */
const mfaEnrolledFactorsAtom = atom((get) => {
    const { resolver } = get(mfaChallengeBaseAtom);
    if (!resolver)
        return [];
    return resolver.hints.map((hint) => {
        const factor = {
            factorId: hint.factorId,
            displayName: hint.displayName || undefined,
            enrollmentTime: hint.enrollmentTime
                ? new Date(hint.enrollmentTime).toLocaleDateString()
                : undefined,
        };
        // Extract phone number if it's a phone factor
        if (hint.factorId === 'phone') {
            const phoneHint = hint;
            factor.phoneNumber = phoneHint.phoneNumber || undefined;
        }
        return factor;
    });
});
/**
 * Derived atom: Available challenge methods based on resolver hints.
 */
const availableMfaMethodsAtom = atom((get) => {
    const factors = get(mfaEnrolledFactorsAtom);
    const methods = [];
    for (const factor of factors) {
        if (factor.factorId === 'phone' && !methods.includes('sms')) {
            methods.push('sms');
        }
        // Email factor would be detected here when supported
        // if (factor.factorId === 'email' && !methods.includes('email')) {
        //   methods.push('email');
        // }
    }
    return methods;
});
/**
 * Derived atom: Complete MFA challenge state for components.
 */
const mfaChallengeStateAtom = atom((get) => {
    const base = get(mfaChallengeBaseAtom);
    return {
        ...base,
        enrolledFactors: get(mfaEnrolledFactorsAtom),
        availableMethods: get(availableMfaMethodsAtom),
    };
});
/**
 * Action atom: Start MFA challenge flow.
 * Called when sign-in receives auth/multi-factor-auth-required error.
 */
const startMfaChallengeAtom = atom(null, (_get, set, resolver) => {
    set(mfaChallengeBaseAtom, {
        isPending: true,
        resolver,
        selectedMethod: null,
        phoneVerificationId: null,
    });
});
/**
 * Action atom: Clear all MFA challenge state.
 * Called after successful MFA verification or on sign-out.
 */
const clearMfaChallengeAtom = atom(null, (_get, set) => {
    set(mfaChallengeBaseAtom, initialState);
});

export { availableMfaMethodsAtom, clearMfaChallengeAtom, mfaChallengeBaseAtom, mfaChallengeStateAtom, mfaEnrolledFactorsAtom, startMfaChallengeAtom };
//# sourceMappingURL=mfaChallengeAtoms.js.map
